import { getBrandName } from '@/data/brands';
import { getEntryVariant, getVariants } from '@/data/products';
import type { Product } from '@/types/catalog';

import type { CatalogFilters, SortValue } from './catalog.types';

/** Strips accents and casing so "racao" matches "ração". */
function normalise(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // combining diacritical marks
    .toLowerCase()
    .trim();
}

/** The text a search query is matched against, cached per product. */
const searchIndex = new WeakMap<Product, string>();

function getSearchText(product: Product): string {
  const cached = searchIndex.get(product);
  if (cached) return cached;

  const text = normalise(
    [
      product.name,
      getBrandName(product.brandId),
      product.flavor,
      product.shortDescription,
      product.variants.map((variant) => variant.label).join(' '),
    ].join(' '),
  );

  searchIndex.set(product, text);
  return text;
}

/** Every term in the query must appear somewhere — an AND search. */
function matchesSearch(product: Product, query: string): boolean {
  const terms = normalise(query).split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;

  const haystack = getSearchText(product);
  return terms.every((term) => haystack.includes(term));
}

function hasOverlap<T>(selected: T[], values: T[]): boolean {
  return selected.length === 0 || values.some((value) => selected.includes(value));
}

function minPrice(product: Product): number {
  return getEntryVariant(product).priceCents;
}

function bestPricePerKg(product: Product): number {
  return getVariants(product).reduce((best, variant) => {
    const perKg = variant.weightKg > 0 ? variant.priceCents / variant.weightKg : Infinity;
    return perKg < best ? perKg : best;
  }, Infinity);
}

function isOnSale(product: Product): boolean {
  return product.variants.some(
    (variant) => variant.compareAtPrice !== undefined && variant.compareAtPrice > variant.price,
  );
}

function isAvailable(product: Product): boolean {
  return product.variants.some((variant) => variant.stock !== 'out-of-stock');
}

/**
 * Relevance ranking used when no explicit sort is chosen: promote the products
 * that carry the store — bestsellers, then new arrivals, then rating.
 */
function relevanceScore(product: Product): number {
  let score = product.rating * 10 + Math.log10(product.reviewCount + 1) * 4;
  if (product.badges?.includes('bestseller')) score += 40;
  if (product.badges?.includes('new')) score += 18;
  if (isOnSale(product)) score += 12;
  if (!isAvailable(product)) score -= 60;
  return score;
}

const COMPARATORS: Record<SortValue, (a: Product, b: Product) => number> = {
  relevance: (a, b) => relevanceScore(b) - relevanceScore(a),
  'price-asc': (a, b) => minPrice(a) - minPrice(b),
  'price-desc': (a, b) => minPrice(b) - minPrice(a),
  'price-per-kg': (a, b) => bestPricePerKg(a) - bestPricePerKg(b),
  rating: (a, b) => b.rating - a.rating || b.reviewCount - a.reviewCount,
  name: (a, b) => a.name.localeCompare(b.name, 'pt-BR'),
};

export function filterProducts(products: Product[], filters: CatalogFilters): Product[] {
  const result = products.filter((product) => {
    if (!matchesSearch(product, filters.search)) return false;
    if (!hasOverlap(filters.pet, [product.petType])) return false;
    if (!hasOverlap(filters.brand, [product.brandId])) return false;
    if (!hasOverlap(filters.stage, product.lifeStages)) return false;
    if (!hasOverlap(filters.size, product.breedSizes)) return false;
    if (!hasOverlap(filters.line, [product.line])) return false;
    if (!hasOverlap(filters.type, [product.foodType])) return false;
    if (!hasOverlap(filters.need, product.needs)) return false;
    if (filters.maxPrice !== null && minPrice(product) > filters.maxPrice) return false;
    if (filters.onlyInStock && !isAvailable(product)) return false;
    if (filters.onlyOnSale && !isOnSale(product)) return false;
    return true;
  });

  // `filter` already returned a fresh array, so sorting in place is safe.
  return result.sort(COMPARATORS[filters.sort]);
}

/** Highest entry price in the catalogue — the ceiling for the price slider. */
export function getPriceCeiling(products: Product[]): number {
  const max = products.reduce((highest, product) => Math.max(highest, minPrice(product)), 0);
  return Math.ceil(max / 10) * 10;
}

export { isOnSale, isAvailable, minPrice, bestPricePerKg };
